import type { DecisionAssistantTurn } from './decision-assistant-turn.model';

export interface DiscoveryQuestion {
  id: string;
  prompt: string;
  answerType: string;
  options: string[];
  required: boolean;
}

export interface DiscoveryAnswer {
  questionId: string;
  values: string[];
  evidenceLinks: string[];
  customText?: string;
}

/** Fields shared by the live projection and one immutable history snapshot. */
export interface DiscoveryAssignmentContent {
  id: string;
  workType: string;
  title: string;
  requestedOutcome: string;
  repository: string;
  sourcePath: string;
  sourceRequirementId: string;
  sourceCommitSha: string;
  sourceContentHash: string;
  sourceExcerpt: string;
  requestedByIdentity: string;
  assigneeIdentity: string;
  acceptanceOwnerIdentity: string;
  questions: DiscoveryQuestion[];
  answers: DiscoveryAnswer[];
  responseRevision: number;
  status: string;
  reviewReason: string | null;
  decisionNotes: string | null;
  visualizerContractJson?: string | null;
  contractHash?: string | null;
  adjustedParametersJson?: string | null;
  decisionStance?: string | null;
  finalSnapshotJson?: string | null;
  decisionHash?: string | null;
  mergeAuthorizerUid?: string | null;
  integrationStatus?: string | null;
  workflowMode: string | null;
  contentRevision: number | null;
  contentChangeReason: string | null;
  presentationRevision?: number | null;
  decisionRegisterBindingJson?: string | null;
  decisionRegisterBindingHash?: string | null;
  presentationJson?: string | null;
  presentationHash?: string | null;
  compositionMode?: string | null;
  compositionProfile?: string | null;
  compositionPromptVersion?: string | null;
  compositionProvider?: string | null;
  compositionModel?: string | null;
  compositionPromptHash?: string | null;
  compositionInputHash?: string | null;
  compositionOutputHash?: string | null;
  compositionProvenanceHash?: string | null;
  compositionModelHash?: string | null;
  authorityOriginJson?: string | null;
  authorityOriginHash?: string | null;
  manualRecoveryGrantId: string | null;
  manualRecoveryPayloadHash: string | null;
  assistantTurns?: DecisionAssistantTurn[];
}

export interface DiscoveryAssignment extends DiscoveryAssignmentContent {
  responseAcceptedBy: string | null;
  pullRequestUrl: string | null;
  pullRequestHeadSha: string | null;
  integratedCommitSha: string | null;
  agentTaskId: string | null;
  createdAt: Date;
  updatedAt: Date;
  submittedAt: Date | null;
  responseAcceptedAt: Date | null;
  integratedAt: Date | null;
}

export interface DiscoveryAssignmentHistorySummary {
  metaLogKey: string;
  changedAt: Date | null;
  changedBy: string | null;
  metaOp: bigint;
  status: string;
  responseRevision: number;
  contentRevision: number;
  workflowMode: string;
  sourceCommitSha: string;
  contractHash: string | null;
  contentChangeReason: string | null;
  manualRecoveryGrantId: string | null;
  manualRecoveryPayloadHash: string | null;
  authorityOriginJson?: string | null;
  authorityOriginHash?: string | null;
}

export interface DiscoveryAssignmentHistorySnapshot extends DiscoveryAssignmentContent {
  metaLogKey: string;
  changedAt: Date | null;
  changedBy: string | null;
  metaOp: bigint;
}

export interface DiscoveryAssignmentHistoryPage {
  history: DiscoveryAssignmentHistorySummary[];
  nextPageToken: string | null;
}

export interface DiscoveryProblem {
  key: string;
  repository: string;
  sourcePath: string;
  name: string;
  sourceExcerpt: string;
  sourceCommitSha: string;
  assignments: DiscoveryAssignment[];
  openCount: number;
}

const SPHERE_FILE = /^(problem|pitch|risks|test_cases)\./;

export const OPEN_STATUSES = [
  'assigned',
  'in_progress',
  'input_required',
  'changes_requested',
  'submitted',
];

/** Problems are identified by the immutable source they delegate from, not by a stored problem id. */
export function problemKeyOf(assignment: DiscoveryAssignment): string {
  return `${assignment.repository}|${assignment.sourcePath}`;
}

/** Derives a readable problem name from the source path, since the contract carries no problem title. */
export function problemNameOf(sourcePath: string): string {
  const parts = sourcePath.split('/').filter(Boolean);
  const file = parts[parts.length - 1] ?? '';
  const base =
    SPHERE_FILE.test(file) && parts.length > 1
      ? parts[parts.length - 2]
      : file.replace(/\.[^.]+$/, '');
  const spaced = base.replace(/[_-]+/g, ' ').trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : sourcePath;
}
